<script setup>
import { computed, onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const axios = window.axios;

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const accountId = computed(() => window.location.pathname.split('/')[3]);

const fixedPublicKey = ref('');
const publicKeyTemplate = ref('{{conversation.display_id}}-{{contact.phone_number}}');
const defaultExpirationMinutes = ref('');
const allowAttachments = ref(true);
const hideTechnicalMetadata = ref(true);
const conversationDisplayId = ref('');
const generatedLink = ref('');
const loading = ref(false);
const generating = ref(false);

const inboxId = computed(() => props.inbox?.id);

const settingsPath = computed(
  () => `/api/v1/accounts/${accountId.value}/public_conversation_link/settings`
);

const linksPath = computed(
  () => `/api/v1/accounts/${accountId.value}/public_conversation_link/links`
);

const loadSettings = async () => {
  if (!inboxId.value) return;

  try {
    loading.value = true;
    const { data } = await axios.get(settingsPath.value, {
      params: { inbox_id: inboxId.value },
    });

    fixedPublicKey.value = data.fixed_public_key || '';
    publicKeyTemplate.value = data.public_key_template || publicKeyTemplate.value;
    defaultExpirationMinutes.value = data.default_expiration_minutes || '';
    allowAttachments.value = data.allow_attachments !== false;
    hideTechnicalMetadata.value = data.hide_technical_metadata !== false;
  } catch (error) {
    useAlert('Unable to load public link settings.');
  } finally {
    loading.value = false;
  }
};

const saveSettings = async () => {
  if (!inboxId.value) return;

  try {
    loading.value = true;
    await axios.put(settingsPath.value, {
      inbox_id: inboxId.value,
      fixed_public_key: fixedPublicKey.value,
      public_key_template: publicKeyTemplate.value,
      default_expiration_minutes: defaultExpirationMinutes.value || null,
      allow_attachments: allowAttachments.value,
      hide_technical_metadata: hideTechnicalMetadata.value,
    });
    useAlert('Public link settings saved.');
  } catch (error) {
    useAlert('Unable to save public link settings.');
  } finally {
    loading.value = false;
  }
};

const generateLink = async () => {
  if (!inboxId.value) return;

  try {
    generating.value = true;
    const { data } = await axios.post(linksPath.value, {
      inbox_id: inboxId.value,
      conversation_display_id: conversationDisplayId.value || undefined,
      expires_in_minutes: defaultExpirationMinutes.value || undefined,
    });

    generatedLink.value = `${window.location.origin}${data.link}`;
  } catch (error) {
    useAlert('Unable to generate public link.');
  } finally {
    generating.value = false;
  }
};

onMounted(loadSettings);
</script>

<template>
  <div class="flex flex-col gap-6 p-2">
    <Input
      v-model="fixedPublicKey"
      label="Fixed public key"
      placeholder="Optional fixed key"
    />

    <Input
      v-model="publicKeyTemplate"
      label="Public key template"
      placeholder="{{conversation.display_id}}-{{contact.phone_number}}"
    />

    <Input
      v-model="defaultExpirationMinutes"
      label="Default expiration (minutes)"
      type="number"
      placeholder="Leave empty for no expiration"
    />

    <div class="flex items-center gap-3">
      <Switch v-model="allowAttachments" />
      <span class="text-sm text-n-slate-12">Allow attachments in public view</span>
    </div>

    <div class="flex items-center gap-3">
      <Switch v-model="hideTechnicalMetadata" />
      <span class="text-sm text-n-slate-12">Hide technical metadata</span>
    </div>

    <div class="flex gap-2">
      <NextButton :is-loading="loading" @click="saveSettings">Save settings</NextButton>
    </div>

    <hr class="border-n-slate-4" />

    <Input
      v-model="conversationDisplayId"
      label="Conversation display ID"
      placeholder="Optional - leave empty to use latest conversation"
    />

    <div class="flex gap-2">
      <NextButton :is-loading="generating" @click="generateLink">Generate public link</NextButton>
    </div>

    <Input
      v-if="generatedLink"
      :model-value="generatedLink"
      label="Generated link"
      readonly
    />
  </div>
</template>
